"use client";

import { TVOverlay } from "@/components/tv-overlay";
import { HIBERNATION_POKU_FILTER_STRONG } from "@/constants/arcade-styles";
import Image from "next/image";

const PRICE_CHART_CARD_STYLE = {
  background: "linear-gradient(135deg, #2a2a32 0%, #0d0d12 100%)",
  boxShadow: `
    0 6px 12px rgba(0,0,0,0.6),
    0 3px 6px rgba(0,0,0,0.7),
    inset 0 3px 3px rgba(255,255,255,0.08),
    inset 0 -3px 3px rgba(0,0,0,0.5),
    inset 0 6px 40px rgba(0,0,0,0.4),
    0 0 28px rgba(62,97,250,0.14)
  `.trim(),
  border: "1px solid rgba(62, 97, 250, 0.22)",
};

/**
 * Henlo TV — dead channel.
 *
 * Operator direction (session-3, mark #1):
 *  "This whole screen is too ALIVE. Get creative with it, you can ditch
 *   the entire design here."
 *
 * Previous design ran a Nickelodeon-bright logo entrance + 14s rotating
 * Poku channel + per-character chromatic catchphrase + amber halo. None
 * of it survived the AFTER HOURS register; it kept reading as "active TV"
 * even after dimming.
 *
 * v2 reimagine: the TV is **off-air**. Static fills the screen, a faint
 * astronaut Poku silhouette is the last frame burned into the phosphor
 * before the broadcast cut, "CH 404 // NO SIGNAL" stamps the bottom in
 * dim cobalt, all flickering dying. No rotation, no catchphrase, no
 * brand mark in the screen. Just the abandoned channel.
 *
 * Pairs with the cosmic carpet visual + cobalt rim chrome canon. The
 * card stays the same outer shell (dark moondust gradient + cobalt rim
 * border + cobalt halo) so it composes with the rest of the right-column
 * stats stack.
 */
export function PriceChartCard() {
  return (
    <div
      className="group relative overflow-hidden rounded-md h-36"
      style={PRICE_CHART_CARD_STYLE}
    >
      <div className="relative h-full">
        {/* TV Overlay — CRT scan + vignette */}
        <TVOverlay intensity="light" />

        {/* CRT static — fine noise pattern. Two layered repeating gradients
            at offset, animated to flicker so the static reads as moving
            random noise. Period-correct for a 2002 CRT after the broadcast
            cuts. */}
        <div
          className="kansei-flicker-a absolute inset-0 z-[1] pointer-events-none"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 2px),
              repeating-linear-gradient(90deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 3px)
            `,
            mixBlendMode: "screen",
          }}
        />

        {/* Burned-in last frame — astronaut silhouette barely visible
            through the static. The phosphor remembers what was on screen
            before the channel cut. */}
        <div className="kansei-flicker-b absolute inset-0 z-[3] flex items-center justify-center pointer-events-none">
          <div className="relative size-20" style={{ opacity: 0.18 }}>
            <Image
              src="/poku/astronaut.png"
              alt=""
              fill
              sizes="80px"
              className="object-contain"
              style={{
                filter: HIBERNATION_POKU_FILTER_STRONG,
              }}
            />
          </div>
        </div>

        {/* Channel ID — top-left corner, tiny, dim cobalt */}
        <div className="kansei-flicker-c absolute top-2 left-2 z-[20]">
          <span
            className="font-pressStart2P text-[6px] tracking-[0.18em]"
            style={{
              color: "rgba(62, 97, 250, 0.55)",
              textShadow: "0 0 1px rgba(62, 97, 250, 0.4)",
            }}
          >
            CH 404
          </span>
        </div>

        {/* NO SIGNAL stamp — bottom-center, slightly bigger, the dominant
            text on the dead channel */}
        <div className="kansei-flicker-a absolute bottom-3 left-1/2 -translate-x-1/2 z-[20]">
          <span
            className="font-pressStart2P text-[8px] tracking-[0.22em] uppercase"
            style={{
              color: "rgba(113, 113, 122, 0.85)",
              textShadow: "0 0 1px rgba(0, 0, 0, 0.8)",
            }}
          >
            NO SIGNAL
          </span>
        </div>

        {/* Inner CRT frame — cobalt rim, replaces the heavy black ring */}
        <div
          className="pointer-events-none absolute inset-0 z-[50] rounded-md"
          style={{
            boxShadow: `
              inset 0 2px 4px rgba(255, 255, 255, 0.08),
              inset 0 4px 20px rgba(0, 0, 0, 0.5),
              inset 0 -4px 16px rgba(0, 0, 0, 0.4),
              inset 0 0 30px rgba(0, 0, 0, 0.3),
              inset 0 0 0 1px rgba(62, 97, 250, 0.18)
            `.trim(),
          }}
        />
      </div>
    </div>
  );
}
